import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface ColorEntry {
  label: string;
  value: string;
}

interface StyleColorPaletteProps {
  colors: ColorEntry[];
  editable?: boolean;
  onChange?: (index: number, value: string) => void;
}

const isValidHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

export function StyleColorPalette({ colors, editable = false, onChange }: StyleColorPaletteProps) {
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleTextChange = (index: number, raw: string) => {
    const value = raw.length > 0 && !raw.startsWith('#') ? `#${raw}` : raw;
    setDrafts(prev => ({ ...prev, [index]: value }));
    if (isValidHex(value)) onChange?.(index, value);
  };

  const handleTextBlur = (index: number) => {
    const draft = drafts[index];
    if (draft !== undefined && !isValidHex(draft)) {
      setDrafts(prev => { const next = { ...prev }; delete next[index]; return next; });
    }
  };

  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
      {colors.map((color, index) => {
        const displayValue = drafts[index] ?? color.value;
        const isInvalid = drafts[index] !== undefined && !isValidHex(drafts[index]);
        const isOpen = openIndex === index;

        return (
          <div key={color.label} className="flex flex-col">
            <div
              className={cn(
                'h-20 rounded-lg w-full border border-black/10 transition-transform',
                editable && 'cursor-pointer hover:scale-[1.03] hover:shadow-md',
                isOpen && 'ring-2 ring-primary ring-offset-1'
              )}
              style={{ backgroundColor: isValidHex(displayValue) ? displayValue : color.value }}
              onClick={() => editable && setOpenIndex(isOpen ? null : index)}
            />
            <div className="mt-1.5 px-0.5">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground truncate">
                {color.label}
              </p>
              <p className="text-xs font-mono text-foreground">{color.value}</p>
            </div>
            {editable && isOpen && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <input
                  type="color"
                  value={color.value}
                  onChange={(e) => {
                    setDrafts(prev => { const next = { ...prev }; delete next[index]; return next; });
                    onChange?.(index, e.target.value);
                  }}
                  className="w-7 h-7 cursor-pointer rounded border-0 p-0 shrink-0"
                />
                <Input
                  value={displayValue}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  onBlur={() => handleTextBlur(index)}
                  className={cn('h-7 font-mono text-xs', isInvalid && 'ring-1 ring-red-400')}
                  maxLength={7}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
